import Bugsnag from "@bugsnag/js";

let started = false;

const getApiKey = () =>
  process.env.NEXT_PUBLIC_BUGSNAG_API_KEY || process.env.BUGSNAG_API_KEY;

export const startBugsnag = () => {
  if (started) return;
  const apiKey = getApiKey();
  if (!apiKey) {
    console.warn("[Bugsnag] Skipped - BUGSNAG_API_KEY not found");
    return;
  }

  Bugsnag.start({
    apiKey,
    appVersion: process.env.npm_package_version,
    releaseStage: process.env.NODE_ENV || "development",
    logger: null, // Disable internal logger to keep console clean
    // Enable breadcrumbs for better error context
    enabledBreadcrumbTypes: [
      "navigation",
      "request",
      "process",
      "log",
      "user",
      "state",
      "error",
      "manual",
    ],
    maxBreadcrumbs: 40,
  });
  started = true;
  console.log("[Bugsnag] Initialized");
};

/**
 * Extract caller information from stack trace for better error context
 */
function extractCallerInfo(): { file?: string; line?: number; column?: number } {
  const stack = new Error().stack;
  if (!stack) return {};

  // Parse stack trace to find the caller (skip this function and notifyError)
  const lines = stack.split("\n");
  // Skip: Error line, extractCallerInfo, notifyError, actual caller
  const callerLine = lines[3] || lines[2];

  if (!callerLine) return {};

  // Extract file:line:column from stack trace
  // Format: "at functionName (file:line:column)" or "at file:line:column"
  const match = callerLine.match(/\((.+):(\d+):(\d+)\)/) || callerLine.match(/at (.+):(\d+):(\d+)/);

  if (match) {
    return {
      file: match[1]?.replace(process.cwd(), "").replace(/^\//, "") || undefined,
      line: parseInt(match[2] || "0"),
      column: parseInt(match[3] || "0"),
    };
  }

  return {};
}

/**
 * Enhanced error notification with automatic context capture
 */
export const notifyError = (
  error: Error | string | unknown,
  context?: Record<string, any>,
  severity: "info" | "warning" | "error" = "error"
) => {
  if (!getApiKey()) return;
  if (!started) startBugsnag();

  // Automatically capture caller information
  const callerInfo = extractCallerInfo();

  let errorObj: Error;
  let originalError: any = null;
  let errorSource = "unknown";

  if (error instanceof Error) {
    errorObj = error;
    errorSource = error.name || "Error";
  } else if (typeof error === "string") {
    errorObj = new Error(error);
    errorObj.name = "ApplicationError";
    errorSource = "string";
    // Capture stack trace for string errors
    if (Error.captureStackTrace) {
      Error.captureStackTrace(errorObj, notifyError);
    }
  } else if (error && typeof error === "object" && "message" in error) {
    // Handle Supabase PostgrestError and similar API errors
    originalError = error;
    const apiError = error as { message: unknown; code?: string; details?: string; hint?: string };
    
    // Create descriptive error message
    let message = String(apiError.message);
    if (apiError.details) message += ` - ${apiError.details}`;
    if (apiError.hint) message += ` (Hint: ${apiError.hint})`;
    
    errorObj = new Error(message);
    errorObj.name = apiError.code || "APIError";
    errorSource = "api";
    
    // Capture stack trace for API errors
    if (Error.captureStackTrace) {
      Error.captureStackTrace(errorObj, notifyError);
    }
  } else {
    errorObj = new Error(`Unexpected error type: ${JSON.stringify(error)}`);
    errorObj.name = "UnknownError";
    errorSource = "unknown";
    originalError = error;
    
    // Capture stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(errorObj, notifyError);
    }
  }

  Bugsnag.notify(errorObj, (event) => {
    event.severity = severity;

    // Add automatic caller context
    if (callerInfo.file) {
      event.addMetadata("caller", {
        file: callerInfo.file,
        line: callerInfo.line,
        column: callerInfo.column,
      });
    }

    // Add error source information
    event.addMetadata("error", {
      source: errorSource,
      originalType: typeof error,
      hasOriginalError: !!originalError,
    });

    // Add original error details if available
    if (originalError) {
      event.addMetadata("originalError", {
        ...originalError,
        stringified: JSON.stringify(originalError, null, 2),
      });
    }

    // Add user-provided context
    if (context) {
      // Separate out common context fields for better organization
      const { source, component, file, userId, orderId, ...customContext } = context;

      if (source || component) {
        event.context = source || component;
      }

      if (file) {
        event.addMetadata("file", typeof file === "string" ? { name: file } : file);
      }

      if (userId) {
        event.setUser(userId);
      }

      if (orderId) {
        event.addMetadata("order", { id: orderId });
      }

      // Add all custom context
      if (Object.keys(customContext).length > 0) {
        event.addMetadata("context", customContext);
      }
    }

    // Add environment info
    event.addMetadata("environment", {
      url: typeof window !== "undefined" ? window.location.href : undefined,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      timestamp: new Date().toISOString(),
    });
  });
};

/**
 * Leave a breadcrumb for tracking user actions
 */
export const leaveBreadcrumb = (
  message: string,
  metadata?: Record<string, any>,
  type?: "navigation" | "request" | "process" | "log" | "user" | "state" | "error" | "manual"
) => {
  if (!started) return;
  Bugsnag.leaveBreadcrumb(message, metadata, type);
};

/**
 * Set user context for all future errors
 */
export const setUser = (id?: string, email?: string, name?: string) => {
  if (!started) return;
  Bugsnag.setUser(id, email, name);
};

/**
 * Clear user context
 */
export const clearUser = () => {
  if (!started) return;
  Bugsnag.setUser(undefined, undefined, undefined);
};

export default Bugsnag;
